import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { buildTemplateSelectItems, getSmartDefaultTemplate } from "@utils/helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Dialog, Select, SelectItem, TabNavigation } from "@fronterahealth/frontera-ui-components";

import {
  ReportTemplateStatus,
  ReportTemplateTypeEdge,
  ReportTypeEnums,
  useCreateReportMutation,
  useReportTemplatesQuery,
} from "@api/graphql/types-and-hooks";
import { NewClientTab } from "@components/CreateNewClientDialog/NewClientTab";
import { SelectClientTab } from "@components/CreateNewClientDialog/SelectClientTab";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

enum DialogTab {
  NewClient = "New Client",
  ExistingClient = "Existing Client",
}

interface CreateNewClientDialogProps {
  setDialogOpen: (open: boolean) => void;
  isDialogOpen: boolean;
}

export const CreateNewClientDialog: React.FC<CreateNewClientDialogProps> = ({ isDialogOpen, setDialogOpen }) => {
  const [currentTab, setCurrentTab] = useState<DialogTab>(DialogTab.NewClient);
  const [learnerId, setLearnerId] = useState<string>();
  const [learnerFileId, setLearnerFileId] = useState<string>();
  const [operationsDisabled, setOperationsDisabled] = useState<boolean>(true);
  const reportType = ReportTypeEnums.InitialAssessment; //TODO: inject
  const go = useNavigate();

  const createReportMutation = useCreateReportMutation({});
  const { isFronteraHealthTypeOrg, isUserEnterpriseAdmin } = useAdminData();
  const flags = useFlags<FeatureFlags>();
  const isDemoOrgMode = !!flags["demo-org-mode"];
  const tabs = useMemo(
    () => (isFronteraHealthTypeOrg ? [DialogTab.NewClient, DialogTab.ExistingClient] : [DialogTab.ExistingClient]),
    [isFronteraHealthTypeOrg],
  );
  const { data: createReportData, isPending, error } = createReportMutation;
  const createReportObject = async () => {
    await createReportMutation.mutateAsync({
      reportType: reportType,
      learnerId: learnerId,
      learnerFileId: learnerFileId,
      reportTemplateId: selectedTemplate?.id,
    });
  };

  useEffect(() => {
    if (error) {
      console.error("Error while creating assessment", error);
      notifyError("Error creating assessment");
    }
  }, [error]);

  useEffect(() => {
    setLearnerId(undefined);
    setCurrentTab(tabs[0]);
    setLearnerFileId(undefined);
    setOperationsDisabled(true);
  }, [isDialogOpen, tabs]);

  useEffect(() => {
    if (!createReportData) return;

    const report = createReportData.createReport;
    const reportStatus = report?.status;

    if (reportStatus) {
      notifySuccess(report?.message || "Assessment created successfully");
      const learnerReport = report?.report;

      if (learnerReport) {
        const { clientFirstName, clientLastName } = learnerReport?.reportClientDetails || {};

        setDialogOpen(false);
        //todo configure depending on reportType
        go(`/assessment-reports/${learnerReport?.id}/client-details`, {
          state: {
            id: learnerReport?.id,
            clientName: `${clientFirstName || "N/A"} ${clientLastName || ""}`,
            lastUpdated: new Date(learnerReport?.lastUpdatedDate) || new Date(),
          },
        });
      }
    } else {
      notifyError(report?.message || "Error creating assessment");
    }
  }, [createReportData]);

  const handleSecondaryButtonClick = () => {
    setDialogOpen(false);
  };

  const [selectedTemplate, setSelectedTemplate] = useState<SelectItem | undefined>();

  const templateStatuses = isUserEnterpriseAdmin
    ? [ReportTemplateStatus.Published, ReportTemplateStatus.Draft]
    : [ReportTemplateStatus.Published];

  const { data: templatesData, isLoading: templatesLoading } = useReportTemplatesQuery(
    {
      filter: {
        reportType: reportType,
        statuses: templateStatuses,
      },
    },
    {
      queryKey: ["get-report-templates-by-type", reportType, isUserEnterpriseAdmin],
    },
  );

  const templateEdges = templatesData?.getReportTemplates?.edges;

  const templateSelections = useMemo(
    () => buildTemplateSelectItems((templateEdges ?? []) as ReportTemplateTypeEdge[], isDemoOrgMode),
    [templateEdges, isDemoOrgMode],
  );

  useEffect(() => {
    if (templateSelections.length > 0 && !selectedTemplate) {
      const smartDefault = getSmartDefaultTemplate(templateSelections);
      if (smartDefault) {
        setSelectedTemplate(smartDefault);
      }
    }
  }, [templateSelections, selectedTemplate]);

  const isSubmitDisabled = operationsDisabled || !selectedTemplate;

  return (
    <Dialog
      title="Select a Client for this report"
      open={isDialogOpen}
      setOpen={setDialogOpen}
      primaryButton={{
        text: isPending ? "Creating" : "Continue",
        onClick: createReportObject,
        disabled: isSubmitDisabled,
      }}
      secondaryButton={{ text: "Cancel", onClick: handleSecondaryButtonClick }}
      size="2xl"
      overflowBehavior={currentTab === DialogTab.NewClient ? "auto" : "show"}
    >
      {tabs.length > 1 && (
        <TabNavigation
          tabs={tabs.map((tabName) => ({
            name: tabName,
            type: "component",
            current: currentTab === tabName,
            Component: (
              <Button
                text={tabName}
                appearance="text"
                className={`hover:text-primary ${currentTab === tabName ? "text-limestone-900" : "text-text-secondary"}`}
                onClick={() => setCurrentTab(tabName)}
              />
            ),
          }))}
        />
      )}
      <div className="mt-5">
        <Select
          title="Select Template"
          isLoading={templatesLoading}
          items={templateSelections}
          placeholderText={
            templateSelections.length > 1 && !selectedTemplate ? "Select a template..." : "Type to find a template"
          }
          selected={selectedTemplate}
          setSelected={setSelectedTemplate}
          enableSearch
        />
      </div>
      <div className="mt-4">
        {currentTab === DialogTab.NewClient && (
          <NewClientTab
            setLearnerId={setLearnerId}
            setOperationsDisabled={setOperationsDisabled}
            setLearnerFileId={setLearnerFileId}
          />
        )}
        {currentTab === DialogTab.ExistingClient && (
          <SelectClientTab
            reportType={reportType}
            existingReportOpened={() => setDialogOpen(false)}
            setLearnerId={setLearnerId}
            setOperationsDisabled={setOperationsDisabled}
          />
        )}
      </div>
    </Dialog>
  );
};
